import { Session, User, createClient } from '@supabase/supabase-js';
import { useState, useEffect, createContext, useCallback } from 'react';
import Logger from '../../utils/logger';
import posthog from 'posthog-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Simple Supabase client (same as main-style)
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

export const AuthContext = createContext<{
  session: Session | null;
  isLoading: boolean;
  user: User | null;
}>({
  session: null,
  isLoading: true,
  user: null,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const updateAuthState = useCallback((newSession: Session | null) => {
    setSession(newSession);
    setUser(newSession?.user || null);
    setIsLoading(false);
  }, []);

  // Restore session on mount
  useEffect(() => {
    let isMounted = true;
    const restoreSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          Logger.error('Error checking auth session', error);
        }
        if (isMounted) updateAuthState(data.session ?? null);
      } catch (error) {
        Logger.error('Error checking auth session', error);
        if (isMounted) updateAuthState(null);
      }
    };
    restoreSession();
    return () => {
      isMounted = false;
    };
  }, [updateAuthState]);

  // Subscribe to auth changes
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, updatedSession) => {
      updateAuthState(updatedSession);
      if (!updatedSession || !updatedSession.user) {
        posthog.reset();
      }
    });
    return () => subscription.unsubscribe();
  }, [updateAuthState]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, isLoading, user }}>{children}</AuthContext.Provider>
  );
}
